import { useRef, useState, useMemo } from "react";
import {
  useChannelStateContext,
  useChatContext,
  AttachmentSelector as DefaultAttachmentSelector,
} from "stream-chat-react";
import {
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
} from "@mui/material";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import { CreatePaymentRequestDrawer } from "Components/Chat/CreatePaymentRequestDrawer";
import { CreateDirectPaymentDrawer } from "Components/Chat/CreateDirectPaymentDrawer";
import { CreateMultiPayerRequestDrawer } from "Components/Chat/CreateMultiPayerRequestDrawer";
import { ChatPaymentRestrictionNotice } from "Components/Chat/ChatPaymentRestrictionNotice";
import { identityUserPublicIdFromStreamId } from "Components/Chat/chatConstants";
import { resolveChatPaymentMenu } from "Components/Chat/chatPaymentRules";
import { useCurrentUserAccessRole } from "Hooks/useCurrentUserAccessRole";
import { useIsFullMoonePersona } from "Hooks/useIsFullMoonePersona";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";
import { useMooneLiteCta } from "Hooks/useMooneLiteCta";
import { ChannelKind } from "Types/Chat/channelKind";
import { Resources, useResource } from "Translations/Resources";

const PAYMENT_MENU_RESOURCES = Resources.Chat.PaymentMenu;

type DrawerState =
  | "closed"
  | "menu"
  | "payment-request"
  | "multi-payer-request"
  | "direct-payment";

/**
 * The payment trigger in the composer: a popover with "send payment" and "pay me", or — when
 * the viewer or the counterpart cannot take part (App ADR-0013) — a notice saying why. Which of
 * the two it is, and which items the menu holds, is `resolveChatPaymentMenu`'s decision; this
 * component only renders it and owns the drawers the items open.
 */
export const AttachmentSelectorWithPaymentRequest = () => {
  const [drawerState, setDrawerState] = useState<DrawerState>("closed");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { channel } = useChannelStateContext();
  const {
    client: { userID },
  } = useChatContext();
  const { t } = useResource();
  const isViewerFullMoone = useIsFullMoonePersona();
  // Read for the copy variant only; the tier itself is the persona's, above.
  const { isLiteBecauseOfBank } = useIsUserInMooneLite();
  // Where a restricted (Lite) viewer is sent to lift the restriction.
  const liteViewerCta = useMooneLiteCta();
  const viewerAccessRole = useCurrentUserAccessRole();

  // A group is a group by its kind, not by its head count: a two-member group still gets the
  // group menu. Channels created before the kind was stamped fall back to the member count.
  const channelKind = channel.data?.channelKind;
  const isGroup = channelKind
    ? channelKind === ChannelKind.Group
    : Object.keys(channel.state.members ?? {}).length > 2;

  // The other member of a 1:1 channel, as Stream holds them; undefined in a group.
  const counterpartMember = useMemo(() => {
    if (isGroup) {
      return undefined;
    }
    const members = Object.values(channel.state.members ?? {});
    if (members.length !== 2) {
      return undefined;
    }
    return members.find((m) => m.user_id !== userID);
  }, [channel.state.members, userID, isGroup]);

  const recipientIdentityUserPublicID = counterpartMember?.user_id
    ? identityUserPublicIdFromStreamId(counterpartMember.user_id)
    : undefined;

  const isOneOnOneChat = recipientIdentityUserPublicID !== undefined;

  const menu = resolveChatPaymentMenu({
    isGroup,
    isOneOnOneChat,
    isViewerFullMoone,
    viewerAccessRole,
    // From the Stream profile the API mirrors the tier onto; undefined on a profile written
    // before the field existed.
    isCounterpartFullMoone: counterpartMember?.user?.isFullMoone,
  });

  const closeDrawer = () => setDrawerState("closed");

  const renderMenuContent = () => {
    if (menu.kind === "viewerRestricted") {
      return (
        <ChatPaymentRestrictionNotice
          message={t(
            isLiteBecauseOfBank
              ? PAYMENT_MENU_RESOURCES.LiteBankRestriction
              : PAYMENT_MENU_RESOURCES.LiteRestriction,
          )}
          cta={liteViewerCta}
        />
      );
    }

    if (menu.kind === "counterpartRestricted") {
      return (
        <ChatPaymentRestrictionNotice
          message={t(PAYMENT_MENU_RESOURCES.RecipientNotFullMoone)}
        />
      );
    }

    const { isDirectPaymentAvailable, isPaymentRequestAvailable } = menu;

    return (
      <List disablePadding>
        {isDirectPaymentAvailable && (
          <ListItemButton
            onClick={() => setDrawerState("direct-payment")}
            sx={{ gap: 3 }}
          >
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <CallMadeIcon />
            </ListItemIcon>
            <ListItemText primary={t(PAYMENT_MENU_RESOURCES.DirectPayment)} />
          </ListItemButton>
        )}
        {/* In a group "pay me" is the multi-payer request (everyone can pay the fixed amount);
            the single-payer request (one transaction, first to pay wins) stays for 1:1 chats. */}
        {isPaymentRequestAvailable && (
          <ListItemButton
            onClick={() =>
              setDrawerState(
                isGroup ? "multi-payer-request" : "payment-request",
              )
            }
            sx={{ gap: 3 }}
          >
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <CallReceivedIcon />
            </ListItemIcon>
            <ListItemText primary={t(PAYMENT_MENU_RESOURCES.PaymentRequest)} />
          </ListItemButton>
        )}
      </List>
    );
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        className="str-chat__payment-request-trigger"
        onClick={() => setDrawerState("menu")}
        size="small"
        sx={{
          bgcolor: "primary.main",
          color: "common.white",
          width: 36,
          height: 36,
          "&:hover": { bgcolor: "primary.dark" },
        }}
      >
        <CurrencyExchangeIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Popover
        open={drawerState === "menu"}
        anchorEl={buttonRef.current}
        onClose={closeDrawer}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {renderMenuContent()}
      </Popover>

      {/* Default file attachment selector — repositioned to the right via CSS */}
      <DefaultAttachmentSelector />
      {isGroup ? (
        <CreateMultiPayerRequestDrawer
          isOpen={drawerState === "multi-payer-request"}
          onClose={closeDrawer}
          channel={channel}
        />
      ) : (
        <CreatePaymentRequestDrawer
          isOpen={drawerState === "payment-request"}
          onClose={closeDrawer}
          channel={channel}
        />
      )}
      {isOneOnOneChat && (
        <CreateDirectPaymentDrawer
          isOpen={drawerState === "direct-payment"}
          onClose={closeDrawer}
          channel={channel}
          recipientIdentityUserPublicID={recipientIdentityUserPublicID}
        />
      )}
    </>
  );
};
